/**
 * Sound settings store — global mute, master volume, per-category volumes.
 *
 * Settings persist to localStorage. Player respects these on every play.
 * Honors `docs/15_analytics_gdpr.md` functional-consent default (on by default
 * because audio is core to the experience, but trivial to mute from the
 * heart-icon settings — see doc 16 §7.3).
 *
 * Categories from `manifest.ts`: 'ui', 'events', 'voice', 'ambient', 'upgrades'.
 * Each can be muted independently, plus a master mute and master volume.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SoundDefinition } from './manifest.js';

type Category = SoundDefinition['category'];
type Tier = SoundDefinition['tier'];

interface SoundSettings {
  /** Master mute — overrides everything. */
  readonly masterMuted: boolean;
  /** Master volume 0..1 — multiplies the per-sound volume. */
  readonly masterVolume: number;
  /** Per-category mute. Useful for "ambient off but tap on" or similar. */
  readonly categoryMuted: Readonly<Record<Category, boolean>>;
  /**
   * Per-tier mute (doc 22 tiers 0-5). Debug-tool primarily — JB
   * 2026-05-25: «мутить отдельные слои чтобы вычислять в разных
   * категориях не оч звуки и избавляться от них».
   */
  readonly tierMuted: Readonly<Record<Tier, boolean>>;

  setMasterMuted(v: boolean): void;
  setMasterVolume(v: number): void;
  setCategoryMuted(cat: Category, muted: boolean): void;
  setTierMuted(tier: Tier, muted: boolean): void;
  /** Compute effective volume for a sound given its definition. 0 if muted. */
  effectiveVolume(def: SoundDefinition): number;
}

const DEFAULTS: Pick<
  SoundSettings,
  'masterMuted' | 'masterVolume' | 'categoryMuted' | 'tierMuted'
> = {
  masterMuted: false,
  // 2026-05-24: was 0.8 — JB reported "очень тихий". Combined with
  // per-event volumes bumped to ~1.0 + loudnorm -9 LUFS source files,
  // default is now full master.
  masterVolume: 1.0,
  categoryMuted: {
    ui: false,
    events: false,
    voice: false,
    ambient: false,
    upgrades: false,
  },
  tierMuted: {
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  },
};

export const useSoundSettings = create<SoundSettings>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,
      setMasterMuted(v) {
        set({ masterMuted: v });
      },
      setMasterVolume(v) {
        set({ masterVolume: Math.min(1, Math.max(0, v)) });
      },
      setCategoryMuted(cat, muted) {
        set((s) => ({ categoryMuted: { ...s.categoryMuted, [cat]: muted } }));
      },
      setTierMuted(tier, muted) {
        set((s) => ({ tierMuted: { ...s.tierMuted, [tier]: muted } }));
      },
      effectiveVolume(def) {
        const s = get();
        if (s.masterMuted) return 0;
        if (s.categoryMuted[def.category]) return 0;
        if (s.tierMuted[def.tier]) return 0;
        return Math.min(1, Math.max(0, def.volume * s.masterVolume));
      },
    }),
    { name: 'sai-sound-settings-v1' },
  ),
);
